<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50">
    <div class="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
      <!-- Loading State -->
      <div v-if="loading" class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <h2 class="text-lg font-medium text-gray-900">Processing invitation...</h2>
      </div>

      <!-- Invitation Details -->
      <div v-else-if="invitation && !error" class="text-center">
        <div class="mb-6">
          <div class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h1 class="text-2xl font-bold text-gray-900 mb-2">Team Invitation</h1>
          <p class="text-gray-600">You've been invited to join a team!</p>
        </div>

        <!-- Invitation Details -->
        <div class="bg-gray-50 rounded-lg p-6 mb-6 text-left">
          <div class="space-y-3">
            <div>
              <span class="text-sm font-medium text-gray-500">Account:</span>
              <p class="text-lg font-semibold text-gray-900">{{ invitation.account?.name || 'Loading...' }}</p>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-500">Your Role:</span>
              <p class="capitalize text-lg font-semibold text-gray-900">{{ invitation.role }}</p>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-500">Invited by:</span>
              <p class="text-lg font-semibold text-gray-900">{{ invitation.invitedByUser?.display_name || invitation.invitedByUser?.email || 'Loading...' }}</p>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-500">Email:</span>
              <p class="text-lg font-semibold text-gray-900">{{ invitation.email }}</p>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="space-y-3">
          <!-- Provider Selection for Sign In -->
          <div v-if="!isAuthenticated" class="space-y-3">
            <p class="text-center text-gray-600 mb-4">Sign in to accept this invitation:</p>
            
            <button
              @click="signInWithProvider('google')"
              class="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <!-- Discord login disabled for now -->
            <!--
            <button
              @click="signInWithProvider('discord')"
              class="w-full flex items-center justify-center px-4 py-3 bg-[#5865F2] text-white rounded-lg hover:bg-[#4752C4] transition-colors"
            >
              <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
              Continue with Discord
            </button>
            -->
          </div>
          
          <div v-else-if="isAuthenticated && canAccept" class="space-y-3">
            <button
              @click="acceptInvitation"
              class="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              Accept Invitation
            </button>
            
            <button
              @click="declineInvitation"
              class="w-full px-4 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium"
            >
              Decline
            </button>
          </div>
          
          <div v-else-if="isAuthenticated && !canAccept" class="text-center">
            <p class="text-red-600 font-medium mb-3">This invitation is for a different email address.</p>
            <button
              @click="signOutAndRetry"
              class="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Sign Out & Try Again
            </button>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center">
        <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 class="text-xl font-bold text-gray-900 mb-2">Invitation Error</h2>
        <p class="text-gray-600 mb-6">{{ error }}</p>
        <button
          @click="goHome"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Go Home
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const loading = ref(true)
const error = ref<string | null>(null)
const invitation = ref<any>(null)
const processing = ref(false)
const success = ref(false)

const invitationId = computed(() => route.params.id as string)
const token = computed(() => route.query.token as string)
const signature = computed(() => route.query.signature as string)

const isAuthenticated = computed(() => authStore.isAuthenticated)
const canAccept = computed(() => {
  if (!isAuthenticated.value || !invitation.value) return false
  return invitation.value.email === authStore.user?.email
})

async function getAuthToken(): Promise<string> {
  // Get the current session token
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token || ''
}

async function loadInvitation() {
  try {
    loading.value = true
    error.value = null

    // Verify basic parameters
    if (!invitationId.value || !token.value) {
      throw new Error('Invalid invitation link')
    }

    console.log('Loading team invite:', {
      id: invitationId.value,
      token: token.value
    });

    // Get invitation details from backend API (public endpoint)
    const response = await fetch(`/api/team-invites/${invitationId.value}?token=${token.value}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to load invitation details');
    }

    const inviteData = await response.json();
    console.log('Loaded invitation from backend:', inviteData);

    // The server already validated the token, so we don't need to check it again
    // The invitation data is safe to use

    // Check if expired
    if (new Date(inviteData.expires_at) < new Date()) {
      throw new Error('This invitation has expired')
    }

    // Check if already used
    if (inviteData.used_at) {
      throw new Error('This invitation has already been used')
    }

    invitation.value = inviteData
    console.log('Loaded invitation:', inviteData)

  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load invitation'
    console.error('Error loading invitation:', err)
  } finally {
    loading.value = false
  }
}

async function signInWithProvider(provider: 'google' | 'discord' | 'slack' | 'github') {
  try {
    // Store the invitation data for after sign-in
    sessionStorage.setItem('team_invite_id', invitationId.value)
    sessionStorage.setItem('team_invite_token', token.value)
    sessionStorage.setItem('team_invite_signature', signature.value)
    
    // Redirect to auth with special redirect
    const redirectUrl = `${window.location.origin}/team-invite/${invitationId.value}?token=${token.value}&signature=${signature.value}`
    sessionStorage.setItem('post_auth_redirect', redirectUrl)
    
    // Start sign-in process
    await authStore.signInWithProvider(provider)
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to sign in'
    console.error('Sign in error:', err)
  }
}

async function signInAndAccept() {
  // This function is no longer needed since we have individual provider buttons
  // But keeping it for backward compatibility if needed
  await signInWithProvider('google')
}

async function acceptInvitation() {
  if (!isAuthenticated.value || !invitation.value || !canAccept.value) return

  processing.value = true
  error.value = null

  try {
    const response = await fetch(`/api/team-invites/${invitationId.value}/accept`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getAuthToken()}`
      },
      body: JSON.stringify({
        token: token.value
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to accept invitation')
    }

    const result = await response.json()
    console.log('Invitation accepted:', result)

    success.value = true
    
    // Refresh the auth store to include the new account
    await authStore.loadUser()
    
    // Redirect to dashboard immediately - user is now a team member
    router.push('/dashboard')

  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to accept invitation'
    console.error('Error accepting invitation:', err)
  } finally {
    processing.value = false
  }
}

async function declineInvitation() {
  // Just redirect to dashboard
  router.push('/dashboard')
}

async function signOutAndRetry() {
  try {
    await authStore.signOut()
    // Reload the page to start fresh
    window.location.reload()
  } catch (err) {
    console.error('Sign out error:', err)
  }
}

function goHome() {
  router.push('/')
}

onMounted(() => {
  loadInvitation()
})
</script>